import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Store, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const CATEGORY_KEYS = {
  seguros: "cat_seguros",
  contabilistas: "cat_contabilistas",
  manutencao: "cat_manutencao",
  credito_habitacao: "cat_credito_habitacao",
  afiliados: "cat_afiliados",
};

export default function Marketplace() {
  const { t } = useI18n();
  const [category, setCategory] = useState("seguros");

  const { data: categoriesData } = useQuery({
    queryKey: ["marketplace-categories"],
    queryFn: async () => (await api.get("/marketplace/categories")).data,
  });
  const { data: partners } = useQuery({
    queryKey: ["marketplace-partners", category],
    queryFn: async () => (await api.get("/marketplace/partners", { params: { category } })).data,
  });

  const categories = categoriesData?.categories || Object.keys(CATEGORY_KEYS);

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Store className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl flex items-center gap-3">
            {t("marketplace_title")}
            <Badge variant="outline" className="rounded-sm text-[hsl(var(--warning))] border-[hsl(var(--warning))] text-[10px]">{t("mock_badge")}</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("marketplace_sub")}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`marketplace-cat-${c}`}
            onClick={() => setCategory(c)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
              category === c
                ? "bg-secondary border-foreground text-foreground font-medium"
                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
            }`}
          >
            {t(CATEGORY_KEYS[c] || c)}
          </button>
        ))}
      </div>

      {(partners || []).length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Store className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {partners.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="p-6 rounded-sm border-border h-full" data-testid={`partner-${p.id}`}>
                <p className="font-display text-lg">{p.name}</p>
                <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                {p.contact && (
                  <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" /> {t("contact_label")}: {p.contact}
                  </div>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
